/* AYOYO motion layer — vanilla, no dependencies.
 *
 * The brief asked for React Bits-style motion. React Bits is a React component
 * library built on GSAP/Motion/Three, so none of it can be installed in a Liquid
 * theme; what is portable is the MOTION, and that is what this file reproduces:
 * a fire-once scroll reveal (AnimatedContent), a word-level blur-in for the hero
 * headline (BlurText), and compositor-only hover transforms. Everything here is
 * IntersectionObserver + CSS custom properties.
 *
 * Rules this file holds to:
 *   - only transform / opacity / filter are animated (never width/height/
 *     letter-spacing, which reflow every frame);
 *   - prefers-reduced-motion snaps to the END state, never leaves opacity 0;
 *   - will-change is set when an element is armed and cleared once it lands,
 *     so we don't hold a GPU layer per card forever;
 *   - init is idempotent and re-runs on shopify:section:load, and in the theme
 *     editor everything is shown immediately.
 */
(function () {
  'use strict';

  var REVEAL_TARGETS = [
    ['.ayoyo-gallery__cell', 70],
    ['.product-card', 90],
    ['.collection-card', 80],
    ['.ayoyo-fnav__col', 70],
    // B2B pages (wholesale / about / faq / shipping / quality / RFQ)
    ['.ayoyo-b2b-reveal', 70],
    // Homepage occasion scenes
    ['.ayoyo-occasion__card', 90]
  ];

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var editor = !!(window.Shopify && window.Shopify.designMode);

  function land(el) {
    el.classList.add('is-revealed');
    el.style.willChange = '';
  }

  /* One observer for the whole document; elements opt in by carrying
     data-ayoyo-reveal. Fires once, then unobserves. */
  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          land(e.target);
          io.unobserve(e.target);
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -100px 0px' })
    : null;

  function arm(el, index, step) {
    if (el.hasAttribute('data-ayoyo-reveal')) return;   // idempotent
    el.setAttribute('data-ayoyo-reveal', '');
    // Reduced motion, no observer support, or the theme editor: show it now.
    if (reduce.matches || !io || editor) { land(el); return; }
    el.style.setProperty('--reveal-delay', (index * step) + 'ms');
    el.style.willChange = 'transform, opacity';
    el.addEventListener('transitionend', function onEnd(ev) {
      if (ev.propertyName !== 'opacity') return;
      el.style.willChange = '';
      el.removeEventListener('transitionend', onEnd);
    });
    io.observe(el);
  }

  function armAll(root) {
    REVEAL_TARGETS.forEach(function (pair) {
      var nodes = (root || document).querySelectorAll(pair[0]);
      // Stagger restarts per group, capped so a long row never crawls.
      Array.prototype.forEach.call(nodes, function (el, i) {
        arm(el, Math.min(i, 5), pair[1]);
      });
    });
  }

  /* Hero headline: split into words and blur each in.
     Must wait for fonts — splitting before Karla's metrics settle produces
     wrong line breaks and a visible reflow that scores as layout shift. */
  function splitHeadline(root) {
    // Horizon renders the slide headline as a <p> inside a .text-block that
    // merely carries an `h1` CLASS - there is no real heading element here, so
    // matching on h1/h2/h3 silently finds nothing.
    var block = (root || document).querySelector('.slide__content .text-block');
    var h = block && block.querySelector('h1, h2, h3, h4, p');
    if (!h || h.hasAttribute('data-ayoyo-split')) return;
    h.setAttribute('data-ayoyo-split', '');
    h.removeAttribute('aria-label');
    if (reduce.matches || editor) return;               // leave the text as-is

    var text = h.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;
    // Screen readers get the whole string from a visually-hidden span; the
    // per-word spans are hidden from them so the headline is not spelled out
    // word by word. aria-label is PROHIBITED on <p> (no role), which is what
    // Horizon renders here - hence a real text node rather than an attribute.
    var frag = document.createDocumentFragment();
    var sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = text;
    frag.appendChild(sr);
    text.split(' ').forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'ayoyo-word';
      span.setAttribute('aria-hidden', 'true');
      span.style.setProperty('--i', i);
      span.textContent = word;
      frag.appendChild(span);
      // A real space between inline-blocks keeps copy-paste intact.
      if (i < text.split(' ').length - 1) frag.appendChild(document.createTextNode(' '));
    });
    h.textContent = '';
    h.appendChild(frag);
    h.classList.add('ayoyo-split');
  }

  function init(root) {
    armAll(root);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { splitHeadline(root); });
    } else {
      splitHeadline(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders a section's markup; re-arm just that subtree.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  // Users toggle the OS setting mid-session; honour it immediately.
  var onChange = function () {
    if (!reduce.matches) return;
    document.querySelectorAll('[data-ayoyo-reveal]').forEach(land);
  };
  if (reduce.addEventListener) reduce.addEventListener('change', onChange);

  /* Public escape hatch: land every pending reveal immediately.
     A scroll reveal starts at opacity 0, so anything that renders the page
     WITHOUT scrolling it captures those sections blank - a full-page
     screenshot, a print/PDF, or a crawler. Those cases need a deterministic
     way to show the finished state rather than a simulated scroll. */
  function revealAll() {
    document.querySelectorAll('[data-ayoyo-reveal]').forEach(function (el) {
      land(el);
      if (io) io.unobserve(el);
    });
  }
  window.AyoyoMotion = { revealAll: revealAll, init: init };

  // Printing never scrolls, so force the end state before the paint.
  if (window.matchMedia) {
    var mqPrint = window.matchMedia('print');
    if (mqPrint.addEventListener) mqPrint.addEventListener('change', function (e) { if (e.matches) revealAll(); });
  }
  window.addEventListener('beforeprint', revealAll);

  /* ---------------------------------------------------------------------
     Deferred product-gallery images.

     product-media.liquid parks src/srcset of every slide except the first in
     data-ayoyo-src / data-ayoyo-srcset. Reason: the slides of a horizontal
     carousel all sit within the browser's lazy-load distance threshold, so
     loading="lazy" did not hold them back and fetchpriority="low" did not
     reorder them - six images competed for the connection and the visible one
     (the LCP element) finished at ~4.1s on throttled 4G against a 2.5s budget.

     They are promoted once the page has finished loading, or immediately if
     the shopper reaches the gallery first, whichever happens sooner. Anything
     that needs the whole gallery present at once - print, a crawler, a
     full-page screenshot - can call AyoyoMotion.loadGalleryImages().
     --------------------------------------------------------------------- */
  var deferredPromoted = false;

  function loadGalleryImages() {
    if (deferredPromoted) return;
    deferredPromoted = true;
    document.querySelectorAll('img[data-ayoyo-deferred]').forEach(function (img) {
      var srcset = img.getAttribute('data-ayoyo-srcset');
      var src = img.getAttribute('data-ayoyo-src');
      // srcset first: setting src alone would start a fetch the srcset then replaces.
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
      img.removeAttribute('data-ayoyo-srcset');
      img.removeAttribute('data-ayoyo-src');
      img.removeAttribute('data-ayoyo-deferred');
      img.removeAttribute('fetchpriority');
    });
  }

  function armDeferredGallery(root) {
    var scope = root || document;
    if (!scope.querySelector || !scope.querySelector('img[data-ayoyo-deferred]')) return;
    // Any intent to look past the first slide promotes them straight away.
    ['pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (evt) {
      document.addEventListener(evt, loadGalleryImages, { once: true, passive: true });
    });
  }

  function promoteAfterLoad() {
    // The point is to stay out of the initial load; one frame after `load`
    // the LCP image has already been painted.
    if (window.requestIdleCallback) {
      window.requestIdleCallback(loadGalleryImages, { timeout: 2000 });
    } else {
      setTimeout(loadGalleryImages, 300);
    }
  }

  armDeferredGallery(document);
  if (document.readyState === 'complete') promoteAfterLoad();
  else window.addEventListener('load', promoteAfterLoad, { once: true });
  document.addEventListener('shopify:section:load', function (e) {
    deferredPromoted = false;
    armDeferredGallery(e.target);
    loadGalleryImages();
  });
  window.addEventListener('beforeprint', loadGalleryImages);
  window.AyoyoMotion.loadGalleryImages = loadGalleryImages;
})();
